import axios from 'axios'
import { uuidv4 } from '/@/utils/uuid'
import { CURRENT_CONFIG } from './config'
import { message } from 'ant-design-vue'
import router from '/@/router'
import { ELocalStorageKey, ERouterName, EUserType } from '/@/types/enums'
import cookies from 'vue-cookies'
import { applyApiCryptoRequestForGk, applyApiCryptoResponseForGk, applyApiCryptResponseAll } from '../rsa/api-crypto'
import { tansParams } from '../insp/insp-alarm'
import cache from '../rsa/cache'
import aes from '../rsa/aes'
export * from './type'

const REQUEST_ID = 'X-Request-Id'

// 全局状态管理
let isRedirecting = false
let pendingRequests: (() => void)[] = []

function getAuthToken () {
  return cookies.get(ELocalStorageKey.Token)
}

const instance = axios.create({
  // withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
  // timeout: 12000,
})

// 清空所有待处理的请求
function clearPendingRequests () {
  pendingRequests.forEach(cancel => cancel())
  pendingRequests = []
}

// 恢复所有请求
function resumeRequests () {
  isRedirecting = false
}

instance.interceptors.request.use(config => {
  // 如果正在跳转登录页，阻止新请求
  if (isRedirecting) {
    return new Promise((resolve, reject) => {
      const cancelToken = new axios.CancelToken(cancel => {
        pendingRequests.push(() => {
          cancel('请求被取消，正在跳转登录页')
        })
      })
      config.cancelToken = cancelToken
      // 注意：这里需要 reject 以阻止请求发出，但 cancelToken 会在取消时自动 reject
      // 为了立即拒绝，也可以直接 reject，但为了统一使用 cancelToken，暂不额外 reject
    })
  }

  // 设置 baseURL
  config.baseURL = CURRENT_CONFIG.baseURL

  // 处理 Token
  const token = getAuthToken()
  const isTokenDisabled = (config.headers || {}).isToken === false
  if (token && !isTokenDisabled) {
    // 根据项目习惯，可以放在 Authorization 或自定义头中
    if (config.customToken) {
      config.headers.Authorization = 'Bearer ' + token
    } else {
      config.headers[ELocalStorageKey.Token] = token
    }
  }

  // ========== 重复提交检测（仅 POST / PUT） ==========
  const isRepeatSubmitDisabled = (config.headers || {}).repeatSubmit === false
  if (!isRepeatSubmitDisabled && (config.method === 'post' || config.method === 'put')) {
    const requestObj = {
      url: config.url,
      data: typeof config.data === 'object' ? JSON.stringify(config.data) : config.data,
      time: new Date().getTime()
    }
    const sessionObj = cache.session.getJSON('sessionObj')
    if (!sessionObj) {
      cache.session.setJSON('sessionObj', requestObj)
    } else {
      const { url: s_url, data: s_data, time: s_time } = sessionObj
      const interval = 1000 // 1秒内重复提交视为无效
      if (s_data === requestObj.data && requestObj.time - s_time < interval && s_url === requestObj.url) {
        const msg = '数据正在处理，请勿重复提交'
        console.warn(`[${s_url}]: ${msg}`)

        // return Promise.reject(new Error(msg))
      } else {
        cache.session.setJSON('sessionObj', requestObj)
      }
    }
  }

  // ========== AES 加密（根据环境变量开关） ==========
  if (import.meta.env.VITE_APP_AES_ENCRYPT_ENABLED === 'true') {
    if (config.data) {
      // 对 POST 请求体加密
      // console.log('原始请求数据:', JSON.stringify(config.data))
      config.data = {
        dataParams: encodeURIComponent(aes.encrypt(JSON.stringify(config.data)))
      }
    } else if (config.params) {
      // 对 GET 参数加密
      // console.log('原始请求参数:', JSON.stringify(config.params))
      config.params = {
        dataParams: encodeURIComponent(aes.encrypt(JSON.stringify(config.params)))
      }
    } else {
      // 无数据时发送一个时间戳占位
      const noData = { noData: new Date().getTime() }
      config.params = {
        dataParams: encodeURIComponent(aes.encrypt(JSON.stringify(noData)))
      }
    }
  }
  // console.log('加密', config)
  // 自定义加密层（如签名、RSA 包装等）
  applyApiCryptoRequestForGk(config)

  // GET 请求：将参数拼接到 URL（加密后参数已放入 config.params.dataParams）
  if (config.method === 'get' && config.params) {
    const queryString = tansParams(config.params)
    if (queryString) {
      config.url = config.url + '?' + queryString
    }
    config.params = {} // 清空 params，避免重复添加到 URL
  }

  // 可选：添加 Request-Id 用于追踪
  // config.headers[REQUEST_ID] = uuidv4()

  return config
}, error => {
  return Promise.reject(error)
})

// ==================== 响应拦截器 ====================
instance.interceptors.response.use(
  async response => {
    // ========== 先进行解密（非二进制数据） ==========
    let decryptedResponse = response
    const isBinary = response.request.responseType === 'blob' || response.request.responseType === 'arraybuffer'
    if (!isBinary) {
      try {
        // 调用自定义解密层（可能返回 Promise）
        const cryptoResult = applyApiCryptResponseAll(response)
        decryptedResponse = cryptoResult || response
        // console.log('cryptoResult', cryptoResult)

        // 注意：decryptedResponse 应保持与 response 相同的结构（拥有 data 属性）
        // 如果你的解密函数直接返回了 data 对象，需要重新包装
        if (decryptedResponse && !decryptedResponse.data && decryptedResponse.code !== undefined) {
          // 如果解密后直接是业务数据，则包装成 axios 响应格式
          decryptedResponse = {
            ...response,
            data: decryptedResponse
          }
        }
      } catch (e) {
        console.error('响应解密失败', e)
        // 解密失败时继续使用原响应，避免阻断业务
        decryptedResponse = response
      }
    }

    // 提取业务数据
    const resData = decryptedResponse.data || decryptedResponse

    // ========== 处理 401 / Token 无效 ==========
    const isUnauthorized = resData.code === 401 || (resData.code === -1 && resData.message === 'Token无效')
    if (isUnauthorized) {
      if (!isRedirecting) {
        isRedirecting = true
        clearPendingRequests()

        // 清空本地存储
        localStorage.clear()
        cookies.remove(ELocalStorageKey.Token)

        console.error('登录失效:', resData)

        // 根据用户类型跳转不同页面
        const flag = Number(localStorage.getItem(ELocalStorageKey.Flag))
        let nextRoute = null
        switch (flag) {
          case EUserType.Web:
            nextRoute = router.push('/' + ERouterName.PROJECT)
            break
          case EUserType.Pilot:
            nextRoute = router.push('/' + ERouterName.PILOT)
            break
          default:
            nextRoute = router.push('/')
        }
        nextRoute?.then(() => {
          setTimeout(() => {
            resumeRequests() // 跳转完成后恢复请求
          }, 100)
        }).catch(() => {
          resumeRequests()
        })
      }
      return Promise.reject(resData)
    }

    // 特殊 code 处理（如 sourceCode=514303 直接返回）
    if (resData.code === -1 && resData.message?.includes('sourceCode=514303')) {
      return decryptedResponse
    }

    // 业务错误（code !== 0）
    if (resData.code && resData.code !== 0) {
      message.error(resData.message || '请求失败')
      return Promise.reject(resData)
    }

    // 成功返回
    return decryptedResponse
  },
  err => {
    // 如果是被取消的请求，直接 reject
    if (axios.isCancel(err)) {
      return Promise.reject(err)
    }

    // HTTP 401 处理（与上面的业务 401 类似）
    if (err.response?.status === 401) {
      if (!isRedirecting) {
        isRedirecting = true
        clearPendingRequests()
        localStorage.clear()
        cookies.remove(ELocalStorageKey.Token)
        console.error(err.response)

        const flag = Number(localStorage.getItem(ELocalStorageKey.Flag))
        let nextRoute = null
        switch (flag) {
          case EUserType.Web:
            nextRoute = router.push('/' + ERouterName.PROJECT)
            break
          case EUserType.Pilot:
            nextRoute = router.push('/' + ERouterName.PILOT)
            break
        }
        nextRoute?.then(() => {
          setTimeout(() => {
            resumeRequests()
          }, 100)
        }).catch(() => {
          resumeRequests()
        })
      }
      return Promise.reject(err)
    }

    // 打印请求 ID（如有）
    const requestId = err?.config?.headers?.[REQUEST_ID]
    if (requestId) {
      console.info(`${REQUEST_ID}: ${requestId}`)
    }
    console.info(`url: ${err?.config?.url} [${err?.config?.method}] \n>>>> err: `, err)

    // 提取错误描述
    let description = '-'
    if (err.response?.data?.message) {
      description = err.response.data.message
    } else if (err.response?.data?.result?.message) {
      description = err.response.data.result.message
    }

    // 网络异常判断
    if (!err.response || !err.response.status) {
      message.error('网络异常，请检查后端服务并重试.')
      return Promise.reject(err)
    }

    if (err.response?.status !== 200) {
      message.error(`ERROR_CODE: ${err.response.status}`)
    } else if (description !== '-') {
      message.error(description)
    }

    return Promise.reject(err)
  }
)

/* instance.interceptors.request.use(
  config => {
    // 如果正在跳转，阻止新请求
    if (isRedirecting) {
      return new Promise((resolve, reject) => {
        const cancelToken = new axios.CancelToken(cancel => {
          pendingRequests.push(() => {
            cancel('请求被取消，正在跳转登录页')
          })
        })
        config.cancelToken = cancelToken
      })
    }

    if (config.customToken) {
      config.headers.Authorization = 'Bearer ' + getAuthToken()
    } else {
      config.headers[ELocalStorageKey.Token] = getAuthToken()
    }
    // config.headers[REQUEST_ID] = uuidv4()
    config.baseURL = CURRENT_CONFIG.baseURL
    // 让每个请求携带自定义token 请根据实际情况自行修改
    return config
  },
  error => {
    return Promise.reject(error)
  },
)

instance.interceptors.response.use(
  response => {
    if (response.data.code === 401 || (response.data.code === -1 && response.data.message === 'Token无效')) {
      if (!isRedirecting) {
        isRedirecting = true
        clearPendingRequests()

        localStorage.clear()
        cookies.remove(ELocalStorageKey.Token)
        console.error(response)

        // 设置跳转完成的回调
        const flag: number = Number(localStorage.getItem(ELocalStorageKey.Flag))
        let next = null
        switch (flag) {
          case EUserType.Web:
            next = router.push('/' + ERouterName.PROJECT)
            break
          case EUserType.Pilot:
            next = router.push('/' + ERouterName.PILOT)
            break
        }
        next.then(() => {
          // 跳转完成后恢复请求
          setTimeout(() => {
            resumeRequests()
          }, 100) // 稍等100ms确保页面已加载
        }).catch(() => {
          resumeRequests()
        })
      }
      return Promise.reject(response.data)
    }

    if (response.data.code === -1 && response.data.message.includes('sourceCode=514303')) {
      return response
    }

    if (response.data.code && response.data.code !== 0) {
      message.error(response.data.message)
      return Promise.reject(response.data)
    }
    return response
  },
  err => {
    // 如果是被取消的请求，直接reject
    if (axios.isCancel(err)) {
      return Promise.reject(err)
    }
    if (err.response?.status === 401) {
      if (!isRedirecting) {
        isRedirecting = true
        clearPendingRequests()

        localStorage.clear()
        cookies.remove(ELocalStorageKey.Token)
        console.error(err.response)

        // 设置跳转完成的回调
        const flag: number = Number(localStorage.getItem(ELocalStorageKey.Flag))
        let next = null
        switch (flag) {
          case EUserType.Web:
            next = router.push('/' + ERouterName.PROJECT)
            break
          case EUserType.Pilot:
            next = router.push('/' + ERouterName.PILOT)
            break
        }
        next.then(() => {
          // 跳转完成后恢复请求
          setTimeout(() => {
            resumeRequests()
          }, 100)
        }).catch(() => {
          resumeRequests()
        })
      }
      return Promise.reject(err)
    }
    const requestId = err?.config?.headers && err?.config?.headers[REQUEST_ID]
    if (requestId) {
      console.info(REQUEST_ID, '：', requestId)
    }
    console.info('url: ', err?.config?.url, `【${err?.config?.method}】 \n>>>> err: `, err)

    let description = '-'
    if (err.response?.data && err.response.data.message) {
      description = err.response.data.message
    }
    if (err.response?.data && err.response.data.result) {
      description = err.response.data.result.message
    }

    if (!err.response || !err.response.status) {
      message.error('网络异常，请检查后端服务并重试.')
      return Promise.reject(err)
    }

    if (err.response?.status !== 200) {
      message.error(`ERROR_CODE: ${err.response?.status}`)
    }

    return Promise.reject(err)
  },
) */

export default instance
